import { Blueprint, InitType } from "../blueprint";
import { InstanceState as InstanceState } from "../../instance-service/instance-state.service";
import { StateServices } from "../../instance-service/i-state-services";
import { Vector2, Vector3 } from "three";
import { RenderServices } from "../../instance-service/i-render-services";
import { Container } from "pixi.js";
import { Surface } from "../../../surface/primitives/surface";
import { CameraState } from "src/app/state/camera/camera.state";
import { Entity } from "src/app/state/entities/entity.model";
import { selectWorldTick10s } from "src/app/state/world/world.selector";
// import { imagesPixi } from "../../blueprints/images/images.pixi";
import { PointerTracker } from "src/app/binst/inputs/primitives/pointer-tracker";
import { Rect } from "src/app/binst/primitives/rect";
import { entityActions } from "src/app/state/entities/entity.actions";
import { degToRad } from "three/src/math/MathUtils.js";
import { selectCameraState } from "src/app/state/camera/camera.selector";
import {InstanceService} from "../../instance-service/instance.service";
import {NetworkServices} from "../../instance-service/i-network-services";
import {IMetadata} from "../../instance-service/i-metadata";
import {Subscription} from "rxjs";
import {BlueprintContext} from "../i-blueprint-context";
import {BlueprintSurface} from "../blueprint-movable";
import {
  selectEntityById,
  selectImageComponent,
  selectReactionsComponent,
  selectTextComponent
} from "../../../../state/entities/entity.selector";
import {ImageProperties} from "../../../../state/entities/component-properties-dto/images.properties.dto";
import {ImageGallery} from "../../../rendering/pixi/drawables/image-gallery.pixi";
import {PixiObject} from "../../../rendering/pixi/primitives/pixi-object";
import moment from "moment";
import {BackgroundDrawable} from "../../../rendering/pixi/drawables/background.pixi";
import {ProfileDrawable} from "../../../rendering/pixi/drawables/profile.pixi";
import {TextBoxDrawable} from "../../../rendering/pixi/drawables/text-box.pixi";
import {SocialBarDrawable} from "../../../rendering/pixi/drawables/social-bar.pixi";
import {ReactionProperties} from "../../../../state/entities/component-properties-dto/reactions.properties.dto";
import {TouchFollower} from "../../../rendering/pixi/drawables/touch-follower.pixi";
import {selectFriendReactionsByEntityId} from "../../../../state/reactions/reactions.selector";
import {FriendReaction} from "../../../../state/reactions/reactions.model";
import {ImageDrawable} from "../../../rendering/pixi/drawables/image.pixi";
import {ImageRow} from "../../../rendering/pixi/drawables/image-row.pixi";
import {PixiZDepthLayers} from "../../../surface/canvas/pixi-z-depth-layers.enum";
import {IconButtonDrawable} from "../../../rendering/pixi/drawables/icon-button.pixi";
import {IconTextButtonDrawable} from "../../../rendering/pixi/drawables/icon-text-button.pixi";
import {ProfileShortDrawable} from "../../../rendering/pixi/drawables/profile-short.pixi";
import {Margin} from "../../../primitives/margin";
import {interfaceActions} from "../../../../state/interface/interface.actions";


export class GalleryBlueprint extends BlueprintSurface {
  override id: string;
  override type: string = "Gallery"

  private images: ImageDrawable[]

  isOwners: boolean
  hasGalleryRow = false

  postWidth = 800
  imageWidth = 600
  textWidth = 600
  textMargin = new Margin({
    left: 40,
    top: 30,
    right: 40,
    bottom: 5
  })
  profileMargin = new Margin({
    left: 30,
    top: 5,
    right: 30,
    bottom: 30
  })
  galleryMargin = new Margin({
    left: 40,
    top: 12,
    right: 40,
    bottom: 0
  })
  buttonsMargin = new Margin({
    left: 40,
    top: 12,
    right: 40,
    bottom: 12
  })

  userLiked = false

  oldDimensions: { width: number, height: number } = null


  faIconTextComment = "\uf075"
  faIconTextHeartOpen = '\uf08a'
  faIconTextShare = '\ue09a'
  faIconEllipsis = "\uf142"
  faIconEllipsisVertical = "\uf141"

  touchFollower: TouchFollower

  override async init(config: {
    initType: InitType,
    metadata: IMetadata
  } & BlueprintContext) {
    // this.debug = true
    this.isOwners = config.metadata.isOwners
    await super.init(config)
    this.log("[Gallery] Starting")
    const bkg = this.applyBackground(this.textWidth, 300)
    this.entity = config.entity
    this.setupReactiveBindings()
    this.log("Handlers configured")
  }

  override destroy() {
    this.surface.destroy()
  }

  override makeSurface(config: { entity: Entity; initType: InitType; stateServices: StateServices; renderServices: RenderServices; }): Promise<Surface> {
    throw new Error("Method not implemented.");
  }

  private setupReactiveBindings() {
    this.subscribe(selectEntityById({entityId: this.entity.id}), (entity) => {
        if (!entity) {
          return
        }
    })
    this.subscribe(selectImageComponent(this.entity.id), async (imageProps: ImageProperties[]) => {
      await this.applyImages(imageProps)
      await this.applyUserProfile()
      await this.applyButtons()
    })
    this.subscribe(selectTextComponent(this.entity.id), async (text: string) => {
      await this.applyText(text)
    })
    this.subscribe(selectFriendReactionsByEntityId(this.entity.id), (reactions: FriendReaction[]) => {
      if (!reactions) {
        return
      }
      this.userLiked = this.didMeReact(reactions)
      if (this.components.has('likeButton')) {
        const likeButton = this.getComponent('likeButton') as IconButtonDrawable
        likeButton.updateBtn(this.faIconTextHeartOpen, this.userLiked)
      }
    })
    this.subscribe(selectFriendReactionsByEntityId(this.entity.id), (friendReactions: FriendReaction[]) => {
      if (!friendReactions) {
        return
      }
    })
  }

  didMeReact(reactions: FriendReaction[]) {
    for (const reaction of reactions) {
      if (reaction.friendId === this.blueprintContext.networkServices.userService.user.id) return true
    }
    return false
  }

  private async applyImages(imageProperties: ImageProperties[]) {
    if (imageProperties.length > 1) {
      this.hasGalleryRow = true
      await this.setComponent("GalleryRow", () => {
        const imageRow = new ImageRow({
          blueprintContext: this.blueprintContext,
          width: 500,
          maxSquareSize: 35,
        })
        imageRow.touchPrimitive.showPointerCursor = true
        imageRow.setImages(imageProperties)
        imageRow.imageSelectedSubject.subscribe((selectedFileId) => {
          this.log(`Selected file id: ${selectedFileId}`)
          const imageViewer = this.getComponent("ImageViewer") as ImageDrawable
          imageViewer.createOrUpdate({
            fileId: selectedFileId,
          })
        })
        return imageRow
      })
    }
    await this.setComponent("ImageViewer", () => {
      const imageDrawable = new ImageDrawable({
        blueprintContext: this.blueprintContext,
        fileId: imageProperties[0].fileId,
        borderRadius: 5,
        preferLandscapeRatio: true,
        fitInSquareSize: this.imageWidth
      })
      imageDrawable.widthHeightSubject.subscribe((dimensions) => {
        console.log("Dimensions changed", dimensions)
        // const newDimens = this.fitInSquare(dimensions.width, dimensions.height)
        // imageDrawable.sprite.width = newDimens.width
        // imageDrawable.sprite.height = newDimens.height
        // imageDrawable.touchContainer.showPointerCursor = true
        // imageDrawable.touchContainer.onPressed(async (pt) => {
        //   console.log("Touched")
        //   return true
        // })
        this.layout()
      })
      return imageDrawable
    })
    await this.layout()
  }


  async applyText(text: string) {
    await this.setComponent("textbox", () => {
      const textBox = new TextBoxDrawable({
        blueprintContext: this.blueprintContext,
        width: this.textWidth - 80,
        height: 600,
        text: text
      })
      return textBox
    })
    await this.layout()
  }

  async applyUserProfile() {
    await this.setComponent("profile", () => {
      const profile = new ProfileShortDrawable({
        blueprintContext: this.blueprintContext,
        isMe: false
      })
      return profile
    })
    await this.layout()
  }

  async applyButtons() {
    await this.setComponent("likeButton", () => {
      const btn =  new IconButtonDrawable({
        colour: "#858585",
        width: 25,
        clickableRadius: 20,
        text: this.faIconTextHeartOpen,
        useSolidIcon: true,
        blueprintContext: this.blueprintContext
      })
      btn.touchPrimitive.onPressed((pt) => {
        console.log("Pressed heart: Liking: " + this.userLiked)
        this.blueprintContext.networkServices.reactionsService.react({
          reactionString: this.faIconTextHeartOpen,
          referenceId: this.blueprintContext.entity.id,
          referenceType: 'entity',
          remove: this.userLiked // If userLiked, removed = true
        })
        return true
      })
      return btn
    })
    await this.setComponent("shareButton", () => {
      const btn =  new IconButtonDrawable({
        colour: "#858585",
        width: 25,
        clickableRadius: 20,
        text: this.faIconTextShare,
        useSolidIcon: true,
        blueprintContext: this.blueprintContext
      })
      btn.touchPrimitive.onPressed((pt) => {
        this.blueprintContext.networkServices.shareService.shareEntity(
            this.blueprintContext.entity,
            "post",
            new Vector2(this.entity.coord.x, this.entity.coord.y),
        )
        return true
      })
      return btn
    })
    await this.setComponent("commentButton", () => {
      return new IconButtonDrawable({
        colour: "#858585",
        width: 25,
        clickableRadius: 20,
        text: this.faIconTextComment,
        useSolidIcon: true,
        blueprintContext: this.blueprintContext
      })
    })
    await this.setComponent("optionsButton", () => {
      const btn =  new IconButtonDrawable({
        colour: "#858585",
        width: 25,
        clickableRadius: 20,
        text: this.faIconEllipsisVertical,
        useSolidIcon: true,
        blueprintContext: this.blueprintContext
      })
      btn.touchPrimitive.onPressed((pt) => {
        this.blueprintContext.stateServices.store.dispatch(interfaceActions.openContextMenu())
        this.blueprintContext.stateServices.router.navigate(
            [{outlets: {'context-menu': "context-post"}}],
            {
              queryParams: {
                entityId: this.blueprintContext.entity.id,
                posX: pt.pos.x - 150,
                posY: pt.pos.y
              },
              queryParamsHandling: 'merge' // optional but common
            })
        return true
      })
      btn.touchPrimitive.showPointerCursor = true
      return btn
    })
    const shareButton = this.getComponent('shareButton')
    const commentButton = this.getComponent('commentButton')
    const likeButton = this.getComponent('likeButton')
    shareButton.touchPrimitive.showPointerCursor = true
    commentButton.touchPrimitive.showPointerCursor = true
    likeButton.touchPrimitive.showPointerCursor = true
    this.layout()
  }


  async setupSurface(width: number, height: number) {
    this.log(`Width: ${width}, height: ${height}`)
    this.surface = await this.renderServices.surfaceService.create({
      id: "gallery-",
      left: 0,
      top: 0,
      width: width,
      height: height + 50 // Allow for social bar at bottom
    })
    this.log("surface created")
    this.setupSurfaceMovementHandlers(this.surface, this.isOwners)
    this.surface.mesh.position.z = 70
    this.surface.stage.zIndex = PixiZDepthLayers.MapReactions
    const componentStrings = [ "background", "ImageViewer", "textbox", "profile", "shareButton", "commentButton", "likeButton", "optionsButton"]
    if (this.hasGalleryRow) {
      componentStrings.push("GalleryRow")
    }
    for (let componentString of componentStrings) {
      const component = this.getComponent(componentString)
      this.surface.addPixiPrimitive(component)
    }
    // this.surface.rect.onHover((pt) => {
    //     this.touchFollower.moveTo(pt.relPos)
    //     return false
    // })
    // this.surface.rect.onPressed((pt) => {
    //   return false
    // })
    this.surface.addPixiPrimitive(this.touchFollower)
  }


  private async layout() {
    const componentStrings = [ "shareButton", "commentButton", "optionsButton", "likeButton", "ImageViewer", "textbox", "background", "profile"]
    if (this.hasGalleryRow) {
      componentStrings.push("GalleryRow")
    }
    if (!this.allComponentsExist(componentStrings)) {return}
    const imageViewer = this.getComponent("ImageViewer")
    const bkg = this.getComponent('background') as BackgroundDrawable
    const textBox = this.getComponent('textbox')
    const shareButton = this.getComponent('shareButton')
    const commentButton = this.getComponent('commentButton')
    const likeButton = this.getComponent('likeButton')
    const optionsButton = this.getComponent('optionsButton')
    const profile = this.getComponent('profile')
    const viewerXDiff = (this.postWidth - imageViewer.width) / 2
    const textBkgXDiff = (this.postWidth - this.textWidth) / 2
    imageViewer.x = viewerXDiff
    let bottomOfGalleryRow = imageViewer.height
    let galleryRowHeight = 0
    if (this.hasGalleryRow) {
      const galleryRow = this.getComponent("GalleryRow")
      galleryRow.x = textBkgXDiff + this.textMargin.left
      galleryRow.y = imageViewer.height + this.galleryMargin.top
      bottomOfGalleryRow = galleryRow.y + galleryRow.height + this.galleryMargin.bottom
      galleryRowHeight = galleryRow.height + this.galleryMargin.vertical
    }
    bkg.x = textBkgXDiff
    textBox.x = textBkgXDiff + this.textMargin.left
    textBox.y = bottomOfGalleryRow + this.textMargin.top
    profile.x = textBkgXDiff + this.profileMargin.left
    profile.y = textBox.y + textBox.height + this.textMargin.bottom + this.profileMargin.top
    shareButton.x = textBkgXDiff + this.textWidth - 60
    shareButton.y = profile.y + 8
    commentButton.x = shareButton.x - 55
    commentButton.y = profile.y + 8
    likeButton.x = commentButton.x - 55
    likeButton.y = profile.y + 8
    optionsButton.x = likeButton.x - 55
    optionsButton.y = profile.y + 8
    bkg.y = imageViewer.height - 20
    const bkgBottom = textBox.y + textBox.height + 20
    bkg.setRect({
      left: 0,
      top: 0,
      width: this.textWidth,
      height: 20 + galleryRowHeight + this.galleryMargin.vertical + textBox.height + this.textMargin.vertical + profile.height + this.profileMargin.vertical
    })
    if (!this.surface) {
      await this.setupSurface(this.postWidth, bkgBottom + 20)
    }
    if (this.oldDimensions) {
      const diff = {
        width: (this.oldDimensions.width - this.postWidth) / 2,
        height: (this.oldDimensions.height - bkgBottom + 20) / 2
      }
      this.surface.mesh.position.x = this.entity.coord.x - (diff.width * this.surface.__surfaceService.pixiToThreeScale())
      this.surface.mesh.position.y = this.entity.coord.y - (diff.height * this.surface.__surfaceService.pixiToThreeScale())
    }
    this.oldDimensions = { width: this.postWidth, height: bkgBottom + 20 }
    this.surface.markForRender()
  }


  applyBackground(width, height) {
    if (this.componentExists("background")) {
      let bkg = this.getComponent('background')
      bkg.destroy()
    }
    let bkg = new BackgroundDrawable({
      id: "Background",
      color: "161616",
      roundness: 20,
      width,
      height,
      zDepth: 10
    })
    this.components.set('background', bkg)
    // this.surface.addPixiPrimitive(bkg)
    return bkg
  }


  override updatePosition(config: { newPos: Vector3; }) {
    if (!this.isMoving) {
      this.surface.mesh.position.x = config.newPos.x;
      this.surface.mesh.position.y = config.newPos.y;
    }
  }


  fitInSquare(width, height) {
    const MAX_SIZE = this.postWidth;
    const ratio = width > height
        ? MAX_SIZE / width
        : MAX_SIZE / height;
    return {
      width: Math.round(width * ratio),
      height: Math.round(height * ratio)
    };
  }

}
